import {
  useAddEvaluationMetricMutation,
  useAddOrganizationRequirementMutation,
  useAddRequirementCompetenciesMutation,
  useAddRequirementMutation,
  useAddRequirementResourcesMutation,
  useAddRequirementTagsMutation,
  useAddTagMutation,
  useDeleteEvaluationMetricMutation,
  useDeleteRequirementCompetencyMutation,
  useDeleteRequirementResourceMutation,
  useDeleteRequirementTagMutation,
  useGetRequirementByIdLazyQuery,
  useGetSpeciesQuery,
  useGetOrgCustomLabelsByOrgQuery,
  useGetOrganizationByIdLazyQuery,
  useGetTagsLazyQuery,
  useUpdateEvaluationMetricManyMutation,
  useUpdateRequirementMutation,
} from "graphql/__generated__/hooks";
import { Messages, NotificationSeverity, REQUIREMENT } from "helpers/constants";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EvaluationMetric } from "types/evaluationMetric";
import { Requirement, RequirementAdd } from "types/requirement";
import { Resource } from "types/resource";
import { Species } from "types/species";
import {
  getEditedEntitiesList,
  getNewEntitiesIds,
  getRemovedEntitiesRelationIds,
} from "utils/FilterEntities";
import { Tag } from "types/tag";
import { useNotifications } from "hooks/useNotifications";
import {
  buildTagIdByName,
  getOrCreateOrgExclusiveTagId,
} from "pages/UploadCSV/UploadCSVRequirements/orgExclusiveTag";

/*
 * useRequirementCreateAndEdit
 * handle all the logic of create and edit Requirement EPA and Competency
 */
export const useRequirementCreateAndEdit = (
  type: string,
  edit: boolean = false,
) => {
  const { requirementId, organizationId } = useParams();
  const isOrgView = useMemo(() => !!organizationId, [organizationId]);
  const navigate = useNavigate();
  const [competencySkills, setCompetencySkills] = useState<Requirement[]>([]);
  const [selectSpeciesOpen, setSelectSpeciesOpen] = useState<boolean>(false);
  const [relevantResourceLinks, setRelevantResourceLinks] = useState<
    Resource[]
  >([]);
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const [evaluationMetrics, setEvaluationMetrics] = useState<
    EvaluationMetric[]
  >([]);
  const [addRequirement] = useAddRequirementMutation();
  const [upadteRequirement] = useUpdateRequirementMutation();
  const [addRequirementResources] = useAddRequirementResourcesMutation();
  const [addRequirementCompetencies] = useAddRequirementCompetenciesMutation();
  const [deleteRequirementResource] = useDeleteRequirementResourceMutation();
  const [deleteRequirementCompetency] =
    useDeleteRequirementCompetencyMutation();
  const [addEvaluationMetric] = useAddEvaluationMetricMutation();
  const [deleteEvaluationMetric] = useDeleteEvaluationMetricMutation();
  const [updateEvaluationMetric] = useUpdateEvaluationMetricManyMutation();
  const [getRequirementById, { data: dataRequirement }] =
    useGetRequirementByIdLazyQuery();
  const [addRequirementTags] = useAddRequirementTagsMutation();
  const [deleteRequirementTag] = useDeleteRequirementTagMutation();
  const [addOrganizationRequirement] = useAddOrganizationRequirementMutation();
  const [getOrganizationById, { data: organizationData }] =
    useGetOrganizationByIdLazyQuery();
  const [getTags, { data: tagsCatalogData }] = useGetTagsLazyQuery();
  const [addTag] = useAddTagMutation();

  const { data: dataSpecies } = useGetSpeciesQuery();
  const species = useMemo(() => {
    return dataSpecies?.species || [];
  }, [dataSpecies]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [originalTags, setOriginalTags] = useState<Tag[]>([]);
  const [dataTags, setDataTags] = useState<
    { requirement_tag_id: string; tag: Tag }[]
  >([]);
  const { notify } = useNotifications();

  // Load organization custom labels (if organization view)
  const { data: orgCustomLabelsData } = useGetOrgCustomLabelsByOrgQuery({
    skip: !organizationId,
    variables: { orgId: organizationId as string },
    fetchPolicy: "no-cache",
  });

  const customLabels = useMemo(() => {
    const list =
      orgCustomLabelsData?.organization_custom_label?.map((ocl: any) =>
        ocl?.custom_label ? ocl.custom_label : null,
      ) || [];
    return (list.filter(Boolean) as any[]) || [];
  }, [orgCustomLabelsData]);

  const organizationName = useMemo(() => {
    return organizationData?.organization_by_pk?.name || null;
  }, [organizationData]);

  const tagIdByName = useMemo(
    () => buildTagIdByName(tagsCatalogData?.tag || []),
    [tagsCatalogData],
  );

  const getRoutePath = () => {
    if (type === REQUIREMENT.REQUIREMENT_EPA) return "epas";
    if (type === REQUIREMENT.REQUIREMENT_COMPETENCY) return "competencies";
    if (type === REQUIREMENT.REQUIREMENT_ITER) return "iters";
    return "requirements";
  };

  const backRoute = isOrgView
    ? `/organization/${organizationId}/${getRoutePath()}`
    : `/${getRoutePath()}`;

  useEffect(() => {
    // if edit mode them get data by requirementId
    if (edit && requirementId) {
      getRequirementById({ variables: { requirementId } });
    }
  }, [requirementId, edit]);

  useEffect(() => {
    if (isOrgView && organizationId) {
      getOrganizationById({
        variables: { organizationId },
      });
      getTags();
    }
  }, [isOrgView, organizationId, getOrganizationById, getTags]);

  useEffect(() => {
    if (dataRequirement) {
      // if edit mode them set the value related with the current requirement
      setRelevantResourceLinks(
        (dataRequirement.requirement_by_pk?.resources.map(
          (resource) => resource.resource,
        ) as Resource[]) || [],
      );
      setCompetencySkills(
        dataRequirement.requirement_competency.map(
          (competency) => competency.competency as Requirement,
        ) || [],
      );
      setEvaluationMetrics(
        dataRequirement.requirement_by_pk?.evaluation_metrics || [],
      );
      const currentTags = dataRequirement.requirement_by_pk?.tags.map(
        (tag) => tag.tag,
      ) as Tag[];
      setTags(currentTags);
      setOriginalTags(currentTags);
      setDataTags(dataRequirement.requirement_by_pk?.tags || []);
      if (dataSpecies?.species) {
        setSelectedSpecies(dataSpecies.species[0] as Species);
      }
    }
  }, [dataRequirement]);

  // manage the resource links
  const editResourceLinks = async (requirementId: string) => {
    // Add new Resources Links with the current requirement
    const newResourcesLinksIds = getNewEntitiesIds(
      (dataRequirement?.requirement_by_pk?.resources.map(
        (r) => r.resource,
      ) as any[]) || [],
      relevantResourceLinks,
      "resource_id",
    );

    const newResourcesLinksData = newResourcesLinksIds.map((resourceId) => ({
      requirement_id: requirementId,
      resource_id: resourceId,
    }));
    if (newResourcesLinksData.length > 0) {
      await addRequirementResources({
        variables: {
          objects: newResourcesLinksData,
        },
      });
    }

    // Remove Resources Links from the current requirement
    const removedResourcesLinksIds = getRemovedEntitiesRelationIds(
      (dataRequirement?.requirement_by_pk?.resources.map(
        (r) => r.resource,
      ) as any[]) || [],
      relevantResourceLinks,
      (dataRequirement?.requirement_by_pk?.resources as any[]) || [],
      "resource_id",
      "requirement_resource_id",
      "resource",
    );

    removedResourcesLinksIds.forEach(async (requirementResourceId) => {
      await deleteRequirementResource({
        variables: {
          id: requirementResourceId,
        },
      });
    });
  };

  const createResourceLinks = async (requirementId: string) => {
    // Add Resource Links
    const newResourcesLinksData = relevantResourceLinks.map((resource) => ({
      requirement_id: requirementId,
      resource_id: resource.resource_id,
    }));
    if (newResourcesLinksData.length > 0) {
      await addRequirementResources({
        variables: {
          objects: newResourcesLinksData,
        },
      });
    }
  };

  // Manage the Competency Skills
  const editCompetencySkills = async (requirementId: string) => {
    // Add new competencies Skill to the current Requirement
    const newCompetencySkillIds = getNewEntitiesIds(
      (dataRequirement?.requirement_competency?.map(
        (c) => c.competency,
      ) as any[]) || [],
      competencySkills,
      "requirement_id",
    );
    const newCompetenciesSkillData = newCompetencySkillIds.map(
      (competencyId) => ({
        requirement_id: requirementId,
        competency_id: competencyId,
      }),
    );

    if (newCompetenciesSkillData.length > 0) {
      await addRequirementCompetencies({
        variables: {
          objects: newCompetenciesSkillData,
        },
      });
    }

    // Remove competencies Skill from the current Requirement
    const removedCompetencySkillIds = getRemovedEntitiesRelationIds(
      (dataRequirement?.requirement_competency.map(
        (r) => r.competency,
      ) as any[]) || [],
      competencySkills,
      (dataRequirement?.requirement_competency as any[]) || [],
      "requirement_id",
      "requirement_competency_id",
      "competency",
    );

    removedCompetencySkillIds.forEach(async (id) => {
      await deleteRequirementCompetency({
        variables: {
          id,
        },
      });
    });
  };

  const createCompetencySkills = async (requirementId: string) => {
    // Add Competency Skill
    const newCompetenciesSkillData = competencySkills.map(
      (competencySkill) => ({
        requirement_id: requirementId,
        competency_id: competencySkill.requirement_id,
      }),
    );
    if (newCompetenciesSkillData.length > 0) {
      await addRequirementCompetencies({
        variables: {
          objects: newCompetenciesSkillData,
        },
      });
    }
  };

  // get formatted data based in the form from formik
  const getFormattedData = (reqValues: RequirementAdd) => {
    return {
      name: reqValues.name,
      description: reqValues.description,
      task_time: `${reqValues.hour}:${reqValues.minutes}`,
      min_small_animals_count: reqValues.min_small_animals_count ?? null,
      min_large_animals_count: reqValues.min_large_animals_count ?? null,
      min_performance: reqValues.min_performance,
      min_score: reqValues.min_score,
      type: type,
      animal_size: reqValues.animal_size ?? null,
      overview: reqValues.overview ?? null,
      requirement_type: reqValues.requirement_type ?? null,
      species_id:
        type === REQUIREMENT.REQUIREMENT_COMPETENCY &&
        selectedSpecies?.species_id
          ? selectedSpecies?.species_id
          : null,
    };
  };

  const createEvaluationMetrics = async (requirementId: string) => {
    const newData = evaluationMetrics.map((competencySkill) => ({
      skill_level: competencySkill.skill_level,
      description: competencySkill.description,
      target_achievement_time: competencySkill.target_achievement_time,
      skill_level_label: competencySkill.skill_level_label || null,
      target_achievement_time_label:
        competencySkill.target_achievement_time_label || null,
      requirement_id: requirementId,
    }));
    await addEvaluationMetric({
      variables: { objects: newData },
    });
  };

  const editEvaluationMetrics = async (requirementId: string) => {
    const newEvaluationMetrics = getNewEntitiesIds(
      (dataRequirement?.requirement_by_pk?.evaluation_metrics as any[]) || [],
      evaluationMetrics,
      "evaluation_metric_id",
    );

    const removedEvaluationMetrics = getRemovedEntitiesRelationIds(
      (dataRequirement?.requirement_by_pk?.evaluation_metrics as any[]) || [],
      evaluationMetrics,
      [],
      "evaluation_metric_id",
      null,
      null,
    );

    const newData = evaluationMetrics
      .filter((metric) =>
        newEvaluationMetrics.includes(metric.evaluation_metric_id),
      )
      .map((competencySkill) => ({
        skill_level: competencySkill.skill_level,
        description: competencySkill.description,
        target_achievement_time: competencySkill.target_achievement_time,
        skill_level_label: competencySkill.skill_level_label || null,
        target_achievement_time_label:
          competencySkill.target_achievement_time_label || null,
        requirement_id: requirementId,
      }));
    if (newData.length > 0) {
      await addEvaluationMetric({
        variables: { objects: newData },
      });
    }

    removedEvaluationMetrics.forEach(async (evaluationMetricId) => {
      await deleteEvaluationMetric({
        variables: { id: evaluationMetricId },
      });
    });

    // Edit Evaluation metrics
    const updatedEvaluationMetricsList = getEditedEntitiesList(
      (dataRequirement?.requirement_by_pk?.evaluation_metrics as any[]) || [],
      evaluationMetrics,
      "evaluation_metric_id",
    );

    const updatedEvaluationMetrics = updatedEvaluationMetricsList
      .filter(
        (metric) => !newEvaluationMetrics.includes(metric.evaluation_metric_id),
      )
      .map((metric) => ({
        where: { evaluation_metric_id: { _eq: metric.evaluation_metric_id } },
        _set: {
          skill_level: metric.skill_level,
          description: metric.description,
          target_achievement_time: metric.target_achievement_time,
          skill_level_label: metric.skill_level_label || null,
          target_achievement_time_label:
            metric.target_achievement_time_label || null,
        },
      }));

    if (updatedEvaluationMetrics.length > 0) {
      await updateEvaluationMetric({
        variables: { updates: updatedEvaluationMetrics },
      });
    }
  };

  const editTags = async (requirementId: string) => {
    const newTagsIds = getNewEntitiesIds(
      originalTags,
      tags,
      "tag_id",
    ) as string[];

    const newTagsData = newTagsIds.map((tagId) => ({
      requirement_id: requirementId,
      tag_id: tagId,
    }));

    if (newTagsData.length > 0) {
      await addRequirementTags({
        variables: {
          objects: newTagsData,
        },
      });
    }

    const removedRequirementTagsIds = getRemovedEntitiesRelationIds(
      originalTags,
      tags,
      dataTags,
      "tag_id",
      "requirement_tag_id",
      "tag",
    ) as string[];

    if (!removedRequirementTagsIds) return;
    for (const requirementTagId of removedRequirementTagsIds) {
      await deleteRequirementTag({
        variables: {
          id: requirementTagId,
        },
      });
    }
  };

  const createTags = async (requirementId: string) => {
    const tagObjects: { requirement_id: string; tag_id: string }[] = [];

    if (isOrgView && organizationName) {
      const exclusiveTagId = await getOrCreateOrgExclusiveTagId({
        organizationName,
        tagIdByName,
        addTag,
        refreshTags: getTags,
      });
      if (exclusiveTagId) {
        tagObjects.push({
          requirement_id: requirementId,
          tag_id: exclusiveTagId,
        });
      }
    }

    tags.forEach((tag) => {
      if (tag.tag_id) {
        tagObjects.push({
          requirement_id: requirementId,
          tag_id: tag.tag_id,
        });
      }
    });

    if (tagObjects.length > 0) {
      await addRequirementTags({
        variables: {
          objects: tagObjects,
        },
      });
    }
  };

  // Handle the creation of a new requirement
  const handleCreateRequirement = async (reqValues: RequirementAdd) => {
    const newData = getFormattedData(reqValues);

    const { data, errors } = await addRequirement({
      variables: newData,
    });

    const requirementId =
      data?.insert_requirement?.returning[0].requirement_id || null;
    if (requirementId) {
      await createResourceLinks(requirementId);
      await createCompetencySkills(requirementId);
      await createEvaluationMetrics(requirementId);
      await createTags(requirementId);

      if (isOrgView) {
        await addOrganizationRequirement({
          variables: { org_id: organizationId, requirement_id: requirementId },
        });
      }
    }

    if (!errors) {
      notify({
        message: Messages.successRequirementCreated,
        severity: NotificationSeverity.success,
      });
      navigate(backRoute);
    }
  };

  // Handle the edition of a selected Requirement based in their ID
  const handleEditRequirement = async (reqValues: any) => {
    const updatedData = getFormattedData(reqValues);

    const { errors } = await upadteRequirement({
      variables: {
        id: requirementId,
        requirement: updatedData,
      },
    });

    if (requirementId) {
      await editResourceLinks(requirementId);
      await editCompetencySkills(requirementId);
      await editEvaluationMetrics(requirementId);
      await editTags(requirementId);
    }

    if (!errors) {
      notify({
        message: Messages.successRequirementUpdated,
        severity: NotificationSeverity.success,
      });
      navigate(backRoute);
    }
  };

  const handleAddTag = async (tag: Tag) => {
    setTags([...tags, tag]);
  };

  const handleDeleteTag = async (tagToRemove: Tag) => {
    const newTags = tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id);
    setTags(newTags);
  };

  return {
    competencySkills,
    isOrgView,
    organizationId,
    customLabels,
    setCompetencySkills,
    relevantResourceLinks,
    setRelevantResourceLinks,
    handleCreateRequirement,
    handleEditRequirement,
    selectedSpecies,
    setSelectedSpecies,
    selectSpeciesOpen,
    setSelectSpeciesOpen,
    species,
    dataRequirement,
    evaluationMetrics,
    setEvaluationMetrics,
    tags,
    handleAddTag,
    handleDeleteTag,
    organizationName,
  };
};
